import * as THREE from 'three'

/** Soft radial blob, used for snow spray and the sun's glow. */
export function radialTexture(size = 64, softness = 0.55): THREE.Texture {
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(softness, 'rgba(255,255,255,0.55)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * The sky.
 *
 * A hand-placed gradient rather than an atmospheric scattering model, but built
 * from what scattering actually produces: a deep, slightly violet zenith, a
 * saturated band through the upper sky, then a rapid desaturation to a pale
 * warm horizon where the air path is longest. The old four-stop ramp went
 * straight from mid-blue to near-white and read as a backdrop; most of what
 * makes a sky look like sky is in the last twenty degrees above the horizon.
 *
 * This also feeds the lighting probe, so the gradient is doing double duty —
 * it is where the blue in shadowed snow comes from.
 */
export function skyTexture(): THREE.Texture {
  const c = document.createElement('canvas')
  c.width = 8
  c.height = 512
  const ctx = c.getContext('2d')!
  const g = ctx.createLinearGradient(0, 0, 0, 512)
  g.addColorStop(0.00, '#2a5f9e') // zenith
  g.addColorStop(0.16, '#3d78b8')
  g.addColorStop(0.34, '#5f9bd2')
  g.addColorStop(0.52, '#8bbbe2')
  g.addColorStop(0.68, '#b4d4ec')
  g.addColorStop(0.80, '#d2e4f2')
  g.addColorStop(0.90, '#e6eff6') // haze band
  g.addColorStop(1.00, '#eef2f4') // horizon
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 8, 512)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * The sun's glow — a disc with a wide falloff, placed in the sky along the sun
 * direction. Drawn as its own billboard rather than painted into the dome
 * texture so it sits exactly where the light actually comes from, which is what
 * makes the haze and the highlights agree with each other.
 */
export function sunGlowTexture(size = 256): THREE.Texture {
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!
  const r = size / 2
  const g = ctx.createRadialGradient(r, r, 0, r, r, r)
  g.addColorStop(0.000, 'rgba(255,252,240,1.00)')
  g.addColorStop(0.045, 'rgba(255,248,226,0.95)') // disc edge
  g.addColorStop(0.10, 'rgba(255,240,206,0.55)')
  g.addColorStop(0.24, 'rgba(255,236,200,0.26)')
  g.addColorStop(0.50, 'rgba(246,236,214,0.10)')
  g.addColorStop(1.000, 'rgba(240,238,230,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}
